import { useEffect, useMemo, useRef, useState } from "react";
import {
    MapContainer,
    TileLayer,
    Marker,
    Popup,
    Polyline,
    Circle,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import api from "../../../api/api";
import geofenceService from "../../../api/geofenceService";
import {
    updateTelemetry,
    getTelemetryHistory,
} from "../../../api/iotDeviceService";
import WebSocketService from "../../../services/WebSocketService";
import { applyIoTLiveUpdate } from "../../../utils/applyIoTLiveUpdate";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import {
    getDeliveryStatusLabel,
    getDeliveryStatusColor,
    formatEta,
    formatRemaining,
    formatDistance,
    formatSpeed,
} from "../../../utils/iotDisplayHelpers";
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: markerIcon2x,
    iconUrl: markerIcon,
    shadowUrl: markerShadow,
});

function isOnline(lastUpdated) {
    if (!lastUpdated) return false;
    return Date.now() - new Date(lastUpdated).getTime() < 2 * 60 * 1000;
}

function distanceMeters(lat1, lon1, lat2, lon2) {
    const R = 6371000;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;

    const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) ** 2;

    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export default function IoTMapPage() {
    const [devices, setDevices] = useState([]);
    const [paths, setPaths] = useState({});
    const [zones, setZones] = useState([]);
    const [alerts, setAlerts] = useState([]);
    const [alertedDevices, setAlertedDevices] = useState({});
    const loadTelemetryHistory = async (device) => {
        try {
            const history = await getTelemetryHistory(device.id);

            setSelectedDevice(device);
            setSelectedHistory(Array.isArray(history) ? history : []);
        } catch (err) {
            console.error("Failed to load telemetry history", err);
        }
    };
    const replayTimerRef = useRef(null);
    const wsMapRef = useRef({});
    const zonesRef = useRef([]);
    const addAlert = (text, trackingNumber) => {
        setAlerts((prev) => [
            {
                id: `${Date.now()}-${Math.random()}`,
                text,
                time: new Date().toLocaleTimeString(),
            },
            ...prev,
        ]);

        if (trackingNumber) {
            setAlertedDevices((prev) => ({
                ...prev,
                [trackingNumber]: true,
            }));
        }
    };
    useEffect(() => {
        zonesRef.current = zones;
    }, [zones]);

    

    const handleLiveMessage = (raw) => {
        try {
            const msg = JSON.parse(raw);

            if (msg.type !== "iot_update") return;
            if (!msg.trackingNumber) return;

            setDevices((prev) => applyIoTLiveUpdate(prev, msg));

            if (
                typeof msg.latitude === "number" &&
                typeof msg.longitude === "number"
            ) {
                setPaths((prev) => {
                    const next = { ...prev };
                    const arr = next[msg.trackingNumber]
                        ? [...next[msg.trackingNumber]]
                        : [];

                    arr.push([msg.latitude, msg.longitude]);
                    if (arr.length > 500) arr.shift();

                    next[msg.trackingNumber] = arr;
                    return next;
                });

                zonesRef.current.forEach((z) => {
                    const dist = distanceMeters(
                        msg.latitude,
                        msg.longitude,
                        z.centerLat,
                        z.centerLng
                    );

                    if (dist > z.radiusMeters) {
                        addAlert(
                            `Device ${msg.trackingNumber} LEFT zone ${z.name}`,
                            msg.trackingNumber
                        );
                    }
                });
            }
        } catch (e) {
            console.error("WS parse error", e);
        }
    };

    const connectDeviceSockets = (devicesList) => {
        Object.values(wsMapRef.current).forEach((ws) => ws.close());
        wsMapRef.current = {};

        devicesList.forEach((device) => {
            if (!device.trackingNumber) return;

            const ws = new WebSocketService({
                trackingNumber: device.trackingNumber,
            });

            ws.onMessage = handleLiveMessage;
            ws.connect();

            wsMapRef.current[device.trackingNumber] = ws;
        });
    };
    useEffect(() => {
        const loadDevices = async () => {
            try {
                const res = await api.get("/IoTDevice");
                const list = Array.isArray(res.data) ? res.data : [];

                setDevices(list);
                connectDeviceSockets(list);
            } catch (err) {
                console.error("Failed to load devices", err);
            }
        };

        loadDevices();

        return () => {
            Object.values(wsMapRef.current).forEach((ws) => ws.close());
            wsMapRef.current = {};

            if (replayTimerRef.current) {
                clearInterval(replayTimerRef.current);
            }
        };
    }, []);
    useEffect(() => {
        const loadZones = async () => {
            try {
                const data = await geofenceService.getZones();
                setZones(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error("Failed to load geofences", err);
            }
        };

        loadZones();
    }, []);

    const simulateMovement = async () => {
        for (const d of devices) {
            if (
                typeof d.latitude !== "number" ||
                typeof d.longitude !== "number"
            ) {
                continue;
            }

            const newLat = d.latitude + (Math.random() - 0.5) * 0.01;
            const newLng = d.longitude + (Math.random() - 0.5) * 0.01;

            await updateTelemetry(
                d.id,
                newLat,
                newLng,
                8,
                Math.random() * 80,
                Math.random() * 360
            );
        }
    };

    const alertIcon = new L.Icon({
        iconUrl: markerIcon,
        shadowUrl: markerShadow,
        iconSize: [30, 45],
    });
    const [selectedHistory, setSelectedHistory] = useState([]);
    const [selectedDevice, setSelectedDevice] = useState(null);
    const [replayPoint, setReplayPoint] = useState(null);
    const [isReplaying, setIsReplaying] = useState(false);
    const [replayIndex, setReplayIndex] = useState(0);
    const defaultIcon = new L.Icon.Default();
    const replayPoints = [...selectedHistory]
        .filter(
            (p) =>
                typeof p.latitude === "number" &&
                typeof p.longitude === "number"
        )
        .reverse();

    const handleReplaySliderChange = (e) => {
        const index = Number(e.target.value);

        setReplayIndex(index);

        if (replayPoints[index]) {
            setReplayPoint(replayPoints[index]);
            setReplayIndex(index);
        }
    };
    const startReplay = () => {
        if (!selectedHistory || selectedHistory.length < 2) {
            return;
        }

        if (replayTimerRef.current) {
            clearInterval(replayTimerRef.current);
        }

        const points = [...selectedHistory]
            .filter(
                (p) =>
                    typeof p.latitude === "number" &&
                    typeof p.longitude === "number"
            )
            .reverse();

        if (points.length < 2) {
            return;
        }

        let index = 0;
        setReplayIndex(0);
        setIsReplaying(true);
        setReplayPoint(points[0]);
        replayTimerRef.current = setInterval(() => {
            index += 1;

            if (index >= points.length) {
                clearInterval(replayTimerRef.current);
                replayTimerRef.current = null;
                setIsReplaying(false);
                return;
            }

            setReplayPoint(points[index]);
            setReplayIndex(index);
        }, 800);
    };

    const stopReplay = () => {
        if (replayTimerRef.current) {
            clearInterval(replayTimerRef.current);
            replayTimerRef.current = null;
        }

        setIsReplaying(false);
    };

    const center = useMemo(() => {
        const valid = devices.find(
            (d) =>
                typeof d.latitude === "number" &&
                typeof d.longitude === "number"
        );

        return valid ? [valid.latitude, valid.longitude] : [56.9496, 24.1052];
    }, [devices]);
    
    
    return (
        <div style={{ position: "relative", height: "70vh", width: "100%" }}>
            <button
                onClick={simulateMovement}
                style={{
                    position: "absolute",
                    top: 10,
                    left: 10,
                    zIndex: 9999,
                    padding: "8px 12px",
                    background: "#007bff",
                    color: "white",
                    border: "none",
                    borderRadius: "6px",
                    cursor: "pointer",
                }}
            >
                Simulate Movement
            </button>

            <div
                style={{
                    position: "absolute",
                    top: 10,
                    right: 10,
                    zIndex: 9999,
                }}
            >
                {alerts.slice(0, 5).map((a) => (
                    <div
                        key={a.id}
                        style={{
                            background: "#ffdddd",
                            padding: "8px",
                            marginBottom: "5px",
                            border: "1px solid red",
                            borderRadius: "6px",
                            minWidth: "250px",
                        }}
                    >
                        <b>GeoFence Alert</b>
                        <br />
                        {a.text}
                        <br />
                        <small>{a.time}</small>
                    </div>
                ))}
            </div>

            <MapContainer
                center={center}
                zoom={12}
                style={{ height: "100%", width: "100%" }}
            >
                <TileLayer
                    attribution="&copy; OpenStreetMap contributors"
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                
                {zones.map((z) => (
                    <Circle
                        key={z.id}
                        center={[z.centerLat, z.centerLng]}
                        radius={z.radiusMeters}
                        pathOptions={{
                            color: "red",
                            fillColor: "red",
                            fillOpacity: 0.15,
                        }}
                    >
                        <Popup>
                            <b>{z.name}</b>
                            <br />
                            Radius: {z.radiusMeters} m
                        </Popup>
                    </Circle>
                ))}

                {devices
                    .filter(
                        (d) =>
                            typeof d.latitude === "number" &&
                            typeof d.longitude === "number"
                    )
                    .map((d) => (
                        <Marker
                            key={d.trackingNumber || d.id}
                            position={[d.latitude, d.longitude]}
                            icon={
                                alertedDevices[d.trackingNumber]
                                    ? alertIcon
                                    : defaultIcon
                            }
                        >
                            <Popup>
                                <div>
                                    <b>Tracking:</b> {d.trackingNumber}
                                    <br />

                                    <b>Connection:</b>{" "}
                                    {isOnline(d.lastUpdated) ? "Online" : "Offline"}
                                    <br />

                                    <b>Delivery Status:</b>{" "}
                                    <span style={{ color: getDeliveryStatusColor(d.autoStatus) }}>
                                        {getDeliveryStatusLabel(d.autoStatus)}
                                    </span>
                                    <br />

                                    <b>Latitude:</b> {d.latitude}
                                    <br />

                                    <b>Longitude:</b> {d.longitude}
                                    <br />

                                    <b>Speed:</b>{" "}
                                    {typeof d.speedKmh === "number"
                                        ? `${d.speedKmh.toFixed(1)} km/h`
                                        : formatSpeed(d.stats)}
                                    <br />

                                    <b>Heading:</b>{" "}
                                    {typeof d.headingDegrees === "number"
                                        ? `${d.headingDegrees.toFixed(0)}°`
                                        : "—"}
                                    <br />

                                    <b>GPS Accuracy:</b>{" "}
                                    {typeof d.accuracyMeters === "number"
                                        ? `${d.accuracyMeters.toFixed(1)} m`
                                        : "—"}
                                    <br />

                                    <b>Signal Quality:</b>{" "}
                                    {typeof d.signalQuality === "number"
                                        ? `${d.signalQuality.toFixed(0)}%`
                                        : "—"}
                                    <br />

                                    <b>Movement:</b>{" "}
                                    <span style={{ color: d.moving ? "green" : "red" }}>
                                        {typeof d.moving === "boolean"
                                            ? d.moving
                                                ? "Moving"
                                                : "Stopped"
                                            : "—"}
                                    </span>
                                    <br />

                                    <b>ETA:</b>{" "}
                                    {typeof d.etaMinutes === "number"
                                        ? `${d.etaMinutes.toFixed(0)} min`
                                        : formatEta(d.eta)}
                                    <br />

                                    <b>Remaining:</b>{" "}
                                    {typeof d.remainingDistanceKm === "number"
                                        ? `${d.remainingDistanceKm.toFixed(2)} km`
                                        : formatRemaining(d.eta)}
                                    <br />

                                    <b>Travelled:</b>{" "}
                                    {typeof d.travelledDistanceKm === "number"
                                        ? `${d.travelledDistanceKm.toFixed(2)} km`
                                        : "—"}
                                    <br />

                                    <b>Arrived:</b> {d.arrived ? "Yes" : "No"}
                                    <br />
                                    <button
                                        onClick={() => loadTelemetryHistory(d)}
                                        style={{
                                            marginTop: "8px",
                                            padding: "6px 10px",
                                            border: "none",
                                            borderRadius: "6px",
                                            background: "#007bff",
                                            color: "white",
                                            cursor: "pointer",
                                        }}
                                    >
                                        Telemetry History
                                    </button>
                                </div>  
                            </Popup>
                        </Marker>
                    ))}
                {replayPoint &&
                    typeof replayPoint.latitude === "number" &&
                    typeof replayPoint.longitude === "number" && (
                        <Marker position={[replayPoint.latitude, replayPoint.longitude]}>
                            <Popup>
                                <b>Replay Position</b>
                                <br />
                                Time: {replayPoint.createdAt || "—"}
                                <br />
                                Speed:{" "}
                                {typeof replayPoint.speedKmh === "number"
                                    ? `${replayPoint.speedKmh.toFixed(1)} km/h`
                                    : "—"}
                            </Popup>
                        </Marker>
                    )}
                {Object.entries(paths).map(([tracking, points]) =>
                    points.length >= 2 ? (
                        <Polyline key={tracking} positions={points} />
                    ) : null
                )}
            </MapContainer>
            {selectedDevice && (
                <div
                    style={{
                        position: "absolute",
                        bottom: 10,
                        left: 10,
                        zIndex: 9999,
                        width: "360px",
                        maxHeight: "260px",
                        overflowY: "auto",
                        background: "white",
                        border: "1px solid #ccc",
                        borderRadius: "8px",
                        padding: "10px",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
                    }}
                >
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <b>Telemetry History</b>
                        <button onClick={() => setSelectedDevice(null)}>×</button>
                    </div>

                    <div style={{ fontSize: "13px", marginBottom: "8px" }}>
                        Tracking: {selectedDevice.trackingNumber || "—"}
                        <div style={{ margin: "8px 0" }}>
                            <button
                                onClick={startReplay}
                                disabled={selectedHistory.length < 2 || isReplaying}
                                style={{
                                    padding: "6px 10px",
                                    border: "none",
                                    borderRadius: "6px",
                                    background: "#28a745",
                                    color: "white",
                                    cursor: "pointer",
                                    marginRight: "6px",
                                }}
                            >
                                Replay Route
                            </button>

                            <button
                                onClick={stopReplay}
                                disabled={!isReplaying}
                                style={{
                                    padding: "6px 10px",
                                    border: "none",
                                    borderRadius: "6px",
                                    background: "#dc3545",
                                    color: "white",
                                    cursor: "pointer",
                                }}
                            >
                                Stop
                            </button>
                            {replayPoints.length > 1 && (
                                <div style={{ marginTop: "8px" }}>
                                    <input
                                        type="range"
                                        min="0"
                                        max={replayPoints.length - 1}
                                        value={replayIndex}
                                        onChange={handleReplaySliderChange}
                                        style={{ width: "100%" }}
                                    />

                                    <div style={{ fontSize: "12px", marginTop: "4px" }}>
                                        Time: {replayPoints[replayIndex]?.createdAt || "—"}
                                        <br />
                                        Point: {replayIndex + 1} / {replayPoints.length}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {selectedHistory.length === 0 ? (
                        <div>No telemetry history found</div>
                    ) : (
                        selectedHistory.slice(0, 20).map((t) => (
                            <div
                                key={t.id}
                                style={{
                                    borderTop: "1px solid #eee",
                                    padding: "6px 0",
                                    fontSize: "12px",
                                }}
                            >
                                <b>{t.createdAt}</b>
                                <br />
                                Lat: {t.latitude}, Lng: {t.longitude}
                                <br />
                                Speed:{" "}
                                {typeof t.speedKmh === "number"
                                    ? `${t.speedKmh.toFixed(1)} km/h`
                                    : "—"}
                                <br />
                                Accuracy:{" "}
                                {typeof t.accuracyMeters === "number"
                                    ? `${t.accuracyMeters.toFixed(1)} m`
                                    : "—"}
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
}
       